import { useEffect, useMemo, useState } from "react";
import { loadUpdates } from "../lib/api";
import { useLocale } from "../lib/LocaleProvider";
import { isSafeHref } from "../lib/sourceLinks";

const READ_KEY = "invictus-tax-law-updates-seen";

function combinedItems(activity) {
  return [
    ...(activity?.new_items || []).map((item) => ({ ...item, changeKind: "new" })),
    ...(activity?.changed_items || []).map((item) => ({ ...item, changeKind: "changed" })),
  ];
}

export default function UpdateCenter() {
  const { text, count: formatCount, languageTag } = useLocale();
  const [updates, setUpdates] = useState(null);
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(() => {
    try {
      return window.localStorage.getItem(READ_KEY) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    loadUpdates().then(setUpdates).catch(() => setUpdates(null));
  }, []);

  const activity = updates?.latest_activity || updates;
  const items = useMemo(() => combinedItems(activity), [activity]);
  const itemCount = (activity?.new_item_count || 0) + (activity?.changed_item_count || 0);
  const activityStamp = activity?.checked_at || updates?.checked_at || "";
  const unread = Boolean(itemCount && activityStamp && activityStamp !== seenAt);

  function openCenter() {
    setOpen(true);
    if (!activityStamp) return;
    setSeenAt(activityStamp);
    try {
      window.localStorage.setItem(READ_KEY, activityStamp);
    } catch {
      // The update center still works when storage is unavailable.
    }
  }

  if (updates?.status !== "ok") return null;
  const firstItem = items[0];

  return (
    <>
      <section className={`update-ribbon ${unread ? "is-unread" : ""}`} aria-label={text("updates.aria")}>
        <button className="update-ribbon-summary" onClick={openCenter} aria-expanded={open}>
          <span className="update-signal" aria-hidden="true" />
          <strong>{unread ? text("updates.new") : text("updates.latest")}</strong>
          <span>{itemCount ? text("updates.count", { count: formatCount(itemCount) }) : text("updates.checked")}</span>
        </button>
        {firstItem && isSafeHref(firstItem.url) && (
          <a className="update-ribbon-headline" href={firstItem.url} target="_blank" rel="noreferrer">
            {firstItem.title}
          </a>
        )}
        <button className="update-ribbon-open" onClick={openCenter}>
          {itemCount ? text("updates.all") : text("updates.status")}
        </button>
      </section>

      {open && (
        <div className="update-center-layer">
          <button className="update-center-backdrop" aria-label={text("updates.close")} onClick={() => setOpen(false)} />
          <aside className="update-center" aria-label={text("updates.dialog")} role="dialog" aria-modal="true">
            <header>
              <div>
                <p>{text("updates.latest")}</p>
                <h2>{text("updates.headline")}</h2>
              </div>
              <button onClick={() => setOpen(false)}>{text("common.close")}</button>
            </header>
            <div className="update-center-status">
              <strong>{itemCount ? text("updates.newItems", { count: formatCount(itemCount) }) : text("updates.noNew")}</strong>
              <time lang={languageTag}>{updates.checked_at ? text("updates.checkedAt", { date: new Date(updates.checked_at).toLocaleString(languageTag) }) : ""}</time>
              <p>{text("updates.disclaimer")}</p>
            </div>
            <ol className="update-center-list">
              {items.map((item) => (
                <li key={`${item.changeKind}-${item.item_id}`}>
                  <span>{item.changeKind === "new" ? text("updates.newKind") : text("updates.changedKind")}</span>
                  {isSafeHref(item.url) ? <a href={item.url} target="_blank" rel="noreferrer">{item.title}</a> : <strong>{item.title}</strong>}
                  {isSafeHref(item.url) && <small>{text("updates.publisher")}</small>}
                </li>
              ))}
              {!items.length && <li className="update-center-empty">{text("updates.empty")}</li>}
            </ol>
          </aside>
        </div>
      )}
    </>
  );
}
